import { useMemo } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router';

export interface UserSettingsSection {
  id: string;
  text: string;
  /** Path segment under `/settings`, e.g. `general` → `/settings/general`. */
  path: string;
  visible?: () => boolean;
}

export interface UserSettingsLayoutProps {
  title?: string;
  sections: UserSettingsSection[];
  /** Base path for settings routes (default `/settings`). */
  basePath?: string;
}

/** HTML settings nav — DevExtreme TreeView can hit React #185 under React 19. */
export function UserSettingsLayout({
  title = 'User Settings',
  sections,
  basePath = '/settings'
}: UserSettingsLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const visibleSections = useMemo(
    () => sections.filter((s) => (s.visible ? s.visible() : true)),
    [sections]
  );

  const selectedId = useMemo(() => {
    const match = visibleSections.find((s) => location.pathname === `${basePath}/${s.path}`);
    return match?.id ?? null;
  }, [location.pathname, visibleSections, basePath]);

  if (location.pathname === basePath || location.pathname === `${basePath}/`) {
    const first = visibleSections[0];
    if (first) {
      return <Navigate to={`${basePath}/${first.path}`} replace />;
    }
  }

  return (
    <div className="user-settings-layout">
      <aside className="user-settings-layout__nav" aria-label="Settings categories">
        <h2 className="user-settings-layout__title">{title}</h2>
        <nav className="user-settings-nav">
          {visibleSections.map((section) => {
            const path = `${basePath}/${section.path}`;
            const selected = section.id === selectedId;
            return (
              <button
                key={section.id}
                type="button"
                className={[
                  'user-settings-nav__item',
                  selected ? 'user-settings-nav__item--selected' : ''
                ]
                  .filter(Boolean)
                  .join(' ')}
                aria-current={selected ? 'page' : undefined}
                onClick={() => navigate(path)}
              >
                {section.text}
              </button>
            );
          })}
        </nav>
      </aside>
      <div className="user-settings-layout__content">
        <div className="user-settings-layout__panel">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
